import apiClient from "./apiClient";

/* ── In-memory GET cache with 5-minute TTL ─────────────────────────────── */
const _cache = {};

const dedupGet = (key, fetchFn, ttl = 300000) => {
  const now = Date.now();
  if (_cache[key] && now - _cache[key].ts < ttl) return _cache[key].promise;
  const promise = fetchFn();
  _cache[key] = { promise, ts: now };
  promise.catch(() => { delete _cache[key]; });
  return promise;
};

export const clearAccountsCache = () => {
  for (const k in _cache) delete _cache[k];
};

/* ================= LOGIN ================= */
export const loginUser = (data) =>
  apiClient.post("/accounts/api/login/", data);

/* ================= LOGOUT ================= */
export const logoutUser = () => {
  clearAccountsCache();
  return apiClient.post("/accounts/api/logout/");
};

/* ================= USERS ================= */
export const getUsers = () =>
  dedupGet("users", () => apiClient.get("/accounts/api/users/"));

export const getUserById = (id) =>
  dedupGet(`user_${id}`, () => apiClient.get(`/accounts/api/users/${id}/`));

export const createUser = (data) => {
  return apiClient.post("/accounts/api/users/", data).finally(() => {
    clearAccountsCache();
  });
};

export const updateUser = (id, data) => {
  return apiClient.patch(`/accounts/api/users/${id}/`, data).finally(() => {
    clearAccountsCache();
  });
};

/**
 * deleteUser
 *
 * Enhancement: now accepts captchaToken and sends it in the request body
 * so the backend can verify it before performing the deletion.
 *
 * The Django DELETE endpoint reads request.data, which DRF populates from
 * the JSON body — no URL or header changes needed.
 *
 * @param {string} id           - employee_id of the user to delete
 * @param {string} captchaToken - reCAPTCHA v2 token from the frontend widget
 */
export const deleteUser = (id, captchaToken) => {
  return apiClient.delete(`/accounts/api/users/${id}/`, {
    data: { captcha_token: captchaToken },
  }).finally(() => {
    clearAccountsCache();
  });
};

export const activateUser = (id) => {
  return apiClient.patch(`/accounts/api/users/${id}/activate_user/`).finally(() => {
    clearAccountsCache();
  });
};

export const getManagers = () =>
  dedupGet("managers", () => apiClient.get("/accounts/api/managers/"));

export const getUsersByManager = (managerId) =>
  dedupGet(`users_by_mgr_${managerId}`, () =>
    apiClient.get("/accounts/api/users/", { params: { manager_id: managerId } })
  );

/* ================= LOCATIONS ================= */
export const getLocations = () =>
  dedupGet("locations", () => apiClient.get("/accounts/api/locations/"));

export const createLocation = (data) => {
  return apiClient.post("/accounts/api/locations/", data).finally(() => {
    clearAccountsCache();
  });
};

// FIXED: PATCH now sends code in the URL, matching accounts/urls.py
export const updateLocation = (code, data) => {
  return apiClient.patch(`/accounts/api/locations/${code}/`, data).finally(() => {
    clearAccountsCache();
  });
};

/* ================= PASSWORD RESET ================= */
export const forgotPassword = (email) =>
  apiClient.post("/accounts/api/forgot-password/", { email });

export const confirmForgotPassword = (uid, token, newPassword) =>
  apiClient.post("/accounts/api/forgot-password/", {
    uidb64: uid,
    token,
    new_password: newPassword,
  });

export const resetPassword = (oldPassword, newPassword) => {
  return apiClient.post(
    "/accounts/api/reset-password/",
    { old_password: oldPassword, new_password: newPassword },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};


/* ─── DASHBOARD ──────────────────────────────────────────────────────────────── */
// Returns: { as_of_date, stats, on_leave_today, upcoming_birthdays }
// Same data for ALL roles — admin, manager, employee
export const getDashboardSummary = () =>
  dedupGet("dashboard_summary", () => apiClient.get("/accounts/api/dashboard/summary/"));

/* ─── MONTHLY LEAVE CALENDAR ─────────────────────────────────────────────────── */
// Returns: { year, month, month_name, total, leaves[] }
// Same data for ALL roles — every user sees full company leave calendar
export const getMonthlyLeaves = (year, month) =>
  dedupGet(`monthly_leaves_${year}_${month}`, () =>
    apiClient.get("/accounts/api/leaves/monthly/", { params: { year, month } })
  );


export const sendBirthdayWish = (employeeId) =>
  apiClient.post("/accounts/api/birthday-wish/", { employee_id: employeeId });

/* ================= CUSTOM EMAIL & TRACKING ================= */
export const sendCustomEmail = (formData) =>
  apiClient.post("/accounts/api/send-email/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const getSentEmails = () =>
  dedupGet("sent_emails", () => apiClient.get("/accounts/api/sent-emails/"));

export const getSentEmailDetail = (id) =>
  dedupGet(`sent_email_${id}`, () =>
    apiClient.get(`/accounts/api/sent-emails/${id}/`)
  );

/* ================= COMPANY POLICIES ================= */
export const getCompanyPolicies = () =>
  dedupGet("company_policies", () => apiClient.get("/accounts/api/company-policies/"));

export const uploadCompanyPolicy = (formData) => {
  return apiClient.post("/accounts/api/company-policies/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).finally(() => {
    clearAccountsCache();
  });
};

export const updateCompanyPolicy = (id, formData) => {
  return apiClient.patch(`/accounts/api/company-policies/${id}/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).finally(() => {
    clearAccountsCache();
  });
};

export const deleteCompanyPolicy = (id) => {
  return apiClient.delete(`/accounts/api/company-policies/${id}/`).finally(() => {
    clearAccountsCache();
  });
};

/* ================= ANNOUNCEMENTS ================= */
export const getAnnouncements = () =>
  dedupGet("announcements", () => apiClient.get("/accounts/api/announcements/"));

export const createAnnouncement = (data) => {
  return apiClient.post("/accounts/api/announcements/", data).finally(() => {
    clearAccountsCache();
  });
};

export const updateAnnouncement = (id, data) => {
  return apiClient.patch(`/accounts/api/announcements/${id}/`, data).finally(() => {
    clearAccountsCache();
  });
};

export const deleteAnnouncement = (id) => {
  return apiClient.delete(`/accounts/api/announcements/${id}/`).finally(() => {
    clearAccountsCache();
  });
};